import { useEffect, useState, useMemo } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import {
  ClipboardCheck,
  AlertCircle,
  RefreshCw,
  Table2,
  CalendarDays,
  DollarSign,
  Receipt,
  Building2,
  ArrowRightLeft,
  TrendingUp,
  Clock,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import {
  fetchDeal,
  fetchScenarios,
  autoProject,
  type ScenarioResponse,
  type ProjectionResult,
} from "../api/client";
import type { Tenant } from "../api/types";
import { useDealContext } from "../hooks/useDealContext";
import LoadingSpinner from "../components/LoadingSpinner";
import EmptyState from "../components/EmptyState";
import RentScheduleTab from "../components/audit/RentScheduleTab";
import RolloverTab from "../components/audit/RolloverTab";
import ReimbursementTab from "../components/audit/ReimbursementTab";
import ExpenseTab from "../components/audit/ExpenseTab";
import OccupancyTab from "../components/audit/OccupancyTab";
import ReturnsTab from "../components/audit/ReturnsTab";
import EventTimeline from "../components/EventTimeline";

// ---------- tab definitions ----------

type TabKey = "rent-schedule" | "reimbursements" | "expenses" | "occupancy" | "rollover" | "returns";

interface TabDef {
  key: TabKey;
  label: string;
  icon: typeof CalendarDays;
  mobileLabel?: string;
}

const TABS: TabDef[] = [
  { key: "rent-schedule", label: "Rent Schedule", icon: CalendarDays },
  { key: "reimbursements", label: "Reimbursements", icon: Receipt, mobileLabel: "Reimb." },
  { key: "expenses", label: "Expenses", icon: DollarSign },
  { key: "occupancy", label: "Occupancy", icon: Building2 },
  { key: "rollover", label: "Rollover", icon: ArrowRightLeft },
  { key: "returns", label: "Returns", icon: TrendingUp },
];

const VALID_TABS = new Set<string>(TABS.map((t) => t.key));

// ---------- main page ----------

export default function AuditPage() {
  const { dealId } = useParams<{ dealId: string }>();
  const { deal, setDeal } = useDealContext();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [projecting, setProjecting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [scenario, setScenario] = useState<ScenarioResponse | null>(null);
  const [showTimeline, setShowTimeline] = useState(false);

  // Read initial tab from query params
  const tabParam = searchParams.get("tab") ?? "";
  const initialTab: TabKey = VALID_TABS.has(tabParam) ? (tabParam as TabKey) : "rent-schedule";
  const [activeTab, setActiveTab] = useState<TabKey>(initialTab);

  // Sync tab when query param changes (e.g., navigating from CashFlowPage)
  useEffect(() => {
    const t = searchParams.get("tab") ?? "";
    if (VALID_TABS.has(t) && t !== activeTab) {
      setActiveTab(t as TabKey);
    }
  }, [searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadData = async () => {
    if (!dealId) return;
    try {
      setLoading(true);
      setError(null);
      // Re-fetch deal to ensure property_data is current (may have been
      // updated by auto-project or document parse since DealLayout loaded)
      const freshDeal = await fetchDeal(dealId);
      setDeal(freshDeal);
      const scenarios = await fetchScenarios(dealId);
      const withResult = scenarios.find((s) => s.projection_result);
      setScenario(withResult || null);
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : "Failed to load audit data";
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [dealId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleAutoProject = async () => {
    if (!dealId) return;
    setProjecting(true);
    try {
      await autoProject(dealId);
      await loadData();
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : "Projection failed";
      setError(msg);
    } finally {
      setProjecting(false);
    }
  };

  const projection: ProjectionResult | null = scenario?.projection_result ?? null;

  // Primary source: deal.property_data.tenants (authoritative, includes all fields)
  // Fallback: extract tenant list from projection's tenant_pgi_details when
  // property_data is stale or missing (e.g. deal was loaded before auto-project ran)
  const propertyTenants: Tenant[] = deal.property_data?.tenants ?? [];
  const tenants: Tenant[] = useMemo(() => {
    if (propertyTenants.length > 0) return propertyTenants;
    if (!projection) return [];
    // Extract unique tenants from Year 1 PGI details as fallback
    const yr1 = (projection.annual_cash_flows ?? [])[0];
    if (!yr1?.tenant_pgi_details) return [];
    return yr1.tenant_pgi_details.map((t) => ({
      tenant_id: t.tenant_id,
      tenant_name: t.tenant_name,
      suite_id: t.suite_id,
      leased_sf: t.leased_sf,
      // Minimal stub fields for tenant selector
      pro_rata_share: null,
      lease_type: "",
      lease_start_date: "",
      lease_expiration_date: "",
      base_rent_annual_psf: t.base_rent_psf,
      rent_escalation_type: "",
      rent_escalation_value: 0,
      cam_structure: { cam_type: "" },
    })) as Tenant[];
  }, [propertyTenants, projection]);

  const holdYears: number = deal.property_data?.hold_period_years ?? 10;

  if (loading) return <LoadingSpinner />;

  if (!deal || !dealId) {
    return (
      <EmptyState
        icon={AlertCircle}
        title="Deal not loaded"
        description="Unable to load deal data. Try refreshing the page."
      />
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Print header */}
      <div className="print-header hidden print:block">
        <h1>{deal.name || "Audit Report"}</h1>
        <p>{deal.address || ""} &middot; {scenario?.name || "Base Case"} Audit</p>
      </div>

      {/* Page header */}
      <div className="flex items-center justify-between print:hidden">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-forest-50 border border-forest-200 flex items-center justify-center">
            <ClipboardCheck className="w-5 h-5 text-forest-600" />
          </div>
          <div>
            <h2 className="font-display text-xl font-semibold text-bark-800">
              Audit Reports
            </h2>
            <p className="text-sm text-bark-400 mt-0.5">
              Detailed audit trail for projection assumptions and calculations
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {projection && dealId && scenario && (
            <button
              onClick={() => setShowTimeline(!showTimeline)}
              className={`flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg border transition-all ${
                showTimeline
                  ? "bg-forest-50 border-forest-300 text-forest-700"
                  : "bg-white border-grove-300 text-bark-500 hover:bg-grove-50 hover:shadow-sm"
              }`}
            >
              <Clock className="w-4 h-4" />
              <span className="hidden sm:inline">View Events</span>
              {showTimeline ? (
                <ChevronUp className="w-3.5 h-3.5" />
              ) : (
                <ChevronDown className="w-3.5 h-3.5" />
              )}
            </button>
          )}
          {!projection && deal.property_data && (
            <button
              onClick={handleAutoProject}
              disabled={projecting}
              className="flex items-center gap-2 px-5 py-2.5 bg-honey-600 hover:bg-honey-500 disabled:opacity-50 text-white font-semibold text-sm rounded-lg transition-all shadow-md hover:shadow-lg"
            >
              <RefreshCw className={`w-4 h-4 ${projecting ? "animate-spin" : ""}`} />
              {projecting ? "Projecting..." : "Run Projection"}
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-xl flex items-center gap-3 text-red-700 text-sm">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          {error}
        </div>
      )}

      {!projection ? (
        <EmptyState
          icon={Table2}
          title="No projection available"
          description="Run a projection from the Pro Forma or Scenarios tab to generate audit data."
        />
      ) : (
        <>
          {/* Scenario context */}
          <div className="text-xs text-bark-400">
            Showing audit for scenario:{" "}
            <span className="font-medium text-bark-600">
              {scenario?.name || "Base Case"}
            </span>
          </div>

          {/* Event Timeline panel (collapsible below header) */}
          {showTimeline && dealId && scenario && (
            <div className="animate-fade-in print:hidden">
              <EventTimeline
                dealId={dealId}
                scenarioId={scenario.id}
                onClose={() => setShowTimeline(false)}
              />
            </div>
          )}

          {/* Tab bar */}
          <nav className="border-b border-grove-300 audit-tab-nav">
            <div className="flex gap-0.5 sm:gap-1 -mb-px overflow-x-auto scrollbar-hide">
              {TABS.map((tab) => {
                const isActive = activeTab === tab.key;
                return (
                  <button
                    key={tab.key}
                    onClick={() => setActiveTab(tab.key)}
                    className={`flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-4 py-3 text-xs sm:text-sm font-medium border-b-2 transition-all duration-200 whitespace-nowrap ${
                      isActive
                        ? "border-forest-700 text-forest-700"
                        : "border-transparent text-bark-400 hover:text-bark-700 hover:border-grove-300"
                    }`}
                  >
                    <tab.icon className="w-4 h-4 flex-shrink-0" />
                    <span className="hidden sm:inline">{tab.label}</span>
                    <span className="sm:hidden">{tab.mobileLabel || tab.label}</span>
                  </button>
                );
              })}
            </div>
          </nav>

          {/* Tab content */}
          <div className="mt-2 audit-tab-content">
            {activeTab === "rent-schedule" && dealId && scenario && (
              <RentScheduleTab
                dealId={dealId}
                scenarioId={scenario.id}
                tenants={tenants}
              />
            )}
            {activeTab === "reimbursements" && dealId && scenario && (
              <ReimbursementTab
                dealId={dealId}
                scenarioId={scenario.id}
                tenants={tenants}
                holdYears={holdYears}
              />
            )}
            {activeTab === "expenses" && dealId && scenario && (
              <ExpenseTab dealId={dealId} scenarioId={scenario.id} />
            )}
            {activeTab === "occupancy" && dealId && scenario && (
              <OccupancyTab dealId={dealId} scenarioId={scenario.id} />
            )}
            {activeTab === "rollover" && <RolloverTab projectionResult={projection} />}
            {activeTab === "returns" && dealId && scenario && (
              <ReturnsTab dealId={dealId} scenarioId={scenario.id} />
            )}
          </div>
        </>
      )}
    </div>
  );
}
